/**
 * Users administration (super_admin). Search + role filter, data table, create-user
 * modal, activate/deactivate with confirm. Loading / empty / error states throughout.
 */
import { useCallback, useEffect, useState } from 'react';
import { UserPlus, Search, RefreshCw, ShieldCheck, Ban, CheckCircle2, Pencil, Trash2 } from 'lucide-react';
import { api } from '../lib/apiClient.js';
import { useAuth } from '../auth/AuthProvider.jsx';
import { useToast } from '../components/feedback/ToastProvider.jsx';
import { useDialog } from '../components/feedback/DialogProvider.jsx';
import { Modal } from '../components/ui/Modal.jsx';
import { Field, TextInput, Select } from '../components/ui/Field.jsx';
import styles from './Users.module.css';

const ROLES = [
  'super_admin',
  'office_staff',
  'approver',
  'senior_approver',
  'field_agent',
  'trustee',
];

const ROLE_LABEL = {
  super_admin: 'Super admin',
  office_staff: 'Office staff',
  approver: 'Approver',
  senior_approver: 'Senior approver',
  field_agent: 'Field agent',
  trustee: 'Trustee',
};

export default function Users() {
  const toast = useToast();
  const { confirm } = useDialog();
  const { user: currentUser } = useAuth();
  const [state, setState] = useState({ status: 'loading', users: [] });
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: 'loading' }));
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (q) params.set('q', q);
      if (roleFilter) params.set('role', roleFilter);
      const data = await api.get(`/users?${params}`);
      setState({ status: 'ready', users: data });
    } catch (err) {
      setState({ status: 'error', users: [], error: err.message });
    }
  }, [q, roleFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250); // debounce search/filter
    return () => clearTimeout(t);
  }, [load]);

  const toggleActive = async (user) => {
    const turningOff = user.active;
    const ok = await confirm({
      title: turningOff ? `Deactivate ${user.name}?` : `Reactivate ${user.name}?`,
      message: turningOff
        ? 'They will be signed out and unable to log in until reactivated.'
        : 'They will be able to sign in again.',
      confirmLabel: turningOff ? 'Deactivate' : 'Reactivate',
      danger: turningOff,
    });
    if (!ok) return;
    try {
      await api.patch(`/users/${user.id}/active`, { active: !user.active });
      toast.success(`${user.name} ${turningOff ? 'deactivated' : 'reactivated'}.`);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (user) => {
    const ok = await confirm({
      title: `Delete ${user.name}?`,
      message: 'This permanently removes the account. Their past audit entries are kept. This cannot be undone.',
      confirmLabel: 'Delete user',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.del(`/users/${user.id}`);
      toast.success(`${user.name} deleted.`);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <p className="eyebrow">Administration</p>
          <h1 className={styles.h1}>Users &amp; roles</h1>
          <p className={styles.lede}>Staff, approvers, trustees and field agents.</p>
        </div>
        <button className={styles.primary} onClick={() => setShowCreate(true)}>
          <UserPlus size={17} /> Add user
        </button>
      </header>

      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <Search size={16} />
          <input
            placeholder="Search name, phone or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className={styles.roleSelect}>
          <option value="">All roles</option>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABEL[r]}
            </option>
          ))}
        </select>
        <button className={styles.ghost} onClick={load} aria-label="Refresh">
          <RefreshCw size={15} />
        </button>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last login</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {state.status === 'loading' &&
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  <td colSpan={6}>
                    <div className={styles.skeletonRow} />
                  </td>
                </tr>
              ))}

            {state.status === 'error' && (
              <tr>
                <td colSpan={6} className={styles.errorCell}>
                  {state.error} — <button onClick={load}>retry</button>
                </td>
              </tr>
            )}

            {state.status === 'ready' && state.users.length === 0 && (
              <tr>
                <td colSpan={6} className={styles.emptyCell}>
                  No users match. Adjust the filters or add a user.
                </td>
              </tr>
            )}

            {state.status === 'ready' &&
              state.users.map((u) => (
                <tr key={u.id}>
                  <td>
                    <div className={styles.nameCell}>
                      <span className={styles.avatar}>{u.name.charAt(0)}</span>
                      <div>
                        <strong>{u.name}</strong>
                        {u.email && <span className={styles.email}>{u.email}</span>}
                      </div>
                    </div>
                  </td>
                  <td className="mono">{u.phone}</td>
                  <td>
                    <span className={styles.roleTag}>
                      <ShieldCheck size={13} /> {ROLE_LABEL[u.role] || u.role}
                    </span>
                  </td>
                  <td>
                    <span className={`${styles.status} ${u.active ? styles.active : styles.inactive}`}>
                      {u.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="mono">
                    {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : '—'}
                  </td>
                  <td>
                    <div className={styles.rowActions}>
                      <button
                        className={styles.rowAction}
                        onClick={() => setEditUser(u)}
                        title="Edit user"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        className={styles.rowAction}
                        onClick={() => toggleActive(u)}
                        title={u.active ? 'Deactivate' : 'Reactivate'}
                      >
                        {u.active ? <Ban size={16} /> : <CheckCircle2 size={16} />}
                      </button>
                      <button
                        className={`${styles.rowAction} ${styles.rowActionDanger}`}
                        onClick={() => remove(u)}
                        disabled={u.id === currentUser?.id}
                        title={u.id === currentUser?.id ? "You can't delete your own account" : 'Delete user'}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <CreateUserModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => {
          setShowCreate(false);
          load();
        }}
      />

      <EditUserModal
        user={editUser}
        onClose={() => setEditUser(null)}
        onSaved={() => {
          setEditUser(null);
          load();
        }}
      />
    </div>
  );
}

function CreateUserModal({ open, onClose, onCreated }) {
  const toast = useToast();
  const [form, setForm] = useState({ name: '', phone: '', email: '', role: 'office_staff', password: '' });
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.name.trim().length < 2) errs.name = 'Enter a name';
    if (!/^[+]?[0-9]{10,15}$/.test(form.phone.trim())) errs.phone = 'Enter a valid phone';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = 'Enter a valid email';
    if (form.password.length < 8) errs.password = 'Min 8 characters';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setBusy(true);
    try {
      await api.post('/users', {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        role: form.role,
        password: form.password,
      });
      toast.success(`${form.name} added.`);
      setForm({ name: '', phone: '', email: '', role: 'office_staff', password: '' });
      onCreated();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add user" width={460}>
      <form onSubmit={submit} id="create-user-form">
        <Field label="Full name" required error={errors.name}>
          <TextInput value={form.name} onChange={set('name')} placeholder="Gurpreet Kaur" />
        </Field>
        <Field label="Phone" required error={errors.phone} hint="Contact number">
          <TextInput value={form.phone} onChange={set('phone')} placeholder="9000000010" inputMode="numeric" />
        </Field>
        <Field label="Email" required error={errors.email} hint="Used to sign in">
          <TextInput value={form.email} onChange={set('email')} placeholder="name@usmeharcare.org" type="email" />
        </Field>
        <Field label="Role" required>
          <Select value={form.role} onChange={set('role')}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Password" required error={errors.password} hint="At least 8 characters">
          <TextInput value={form.password} onChange={set('password')} type="password" autoComplete="new-password" />
        </Field>
      </form>
      <div className={styles.modalActions}>
        <button className={styles.ghost} onClick={onClose} type="button">
          Cancel
        </button>
        <button className={styles.primary} form="create-user-form" type="submit" disabled={busy}>
          {busy ? 'Adding…' : 'Add user'}
        </button>
      </div>
    </Modal>
  );
}

function EditUserModal({ user, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({ name: '', email: '', role: 'office_staff', password: '' });
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      setForm({ name: user.name || '', email: user.email || '', role: user.role, password: '' });
      setErrors({});
    }
  }, [user]);

  if (!user) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.name.trim().length < 2) errs.name = 'Enter a name';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = 'Enter a valid email';
    if (form.password && form.password.length < 8) errs.password = 'Min 8 characters';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setBusy(true);
    try {
      const payload = { name: form.name.trim(), email: form.email.trim(), role: form.role };
      if (form.password) payload.password = form.password; // optional reset
      await api.patch(`/users/${user.id}`, payload);
      toast.success(`${form.name} updated.`);
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={!!user} onClose={onClose} title={`Edit ${user.name}`} width={460}>
      <form onSubmit={submit} id="edit-user-form">
        <Field label="Full name" required error={errors.name}>
          <TextInput value={form.name} onChange={set('name')} />
        </Field>
        <Field label="Email" required error={errors.email} hint="Used to sign in">
          <TextInput value={form.email} onChange={set('email')} type="email" />
        </Field>
        <Field label="Role" required hint="Changing the role changes this person's permissions">
          <Select value={form.role} onChange={set('role')}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Reset password" error={errors.password} hint="Leave blank to keep the current password">
          <TextInput value={form.password} onChange={set('password')} type="password" autoComplete="new-password" placeholder="••••••••" />
        </Field>
      </form>
      <div className={styles.modalActions}>
        <button className={styles.ghost} onClick={onClose} type="button">
          Cancel
        </button>
        <button className={styles.primary} form="edit-user-form" type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </Modal>
  );
}
